import { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Button,
    Stack,
    useTheme,
    Chip,
    Card,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import WhiteTooltip from "../../components/WhiteTooltip";
import AgGridTable from "../../components/table/AgGridTable";
import chat_icon from "../../assets/cyber_resilence_dark.svg";
import AntSwitch from "../../components/AntSwitch";
import AddRuleDrawer from "./AddRuleDrawer";
import RuleManagementChatDrawer from "./RuleManagementChatDrawer";
import ActionMenu from "./ActionMenu";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import { maxWidth } from "@mui/system";

const RuleManagement = () => {
    const theme = useTheme();
    const [isAddRuleDrawerOpen, setIsAddRuleDrawerOpen] = useState(false);
    const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false);
    const [rulesData, setRulesData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasActiveAgent, setHasActiveAgent] = useState(false);

    const fetchIdRef = useRef(0);

    const fetchRules = useCallback(async () => {
        const currentFetchId = ++fetchIdRef.current;
        setLoading(true);

        // 1. Fetch Agent status independently to determine if any Active agent exists
        try {
            const agentResponse = await axiosInstance.get(END_POINTS.CLOUD_AGENT);
            if (currentFetchId === fetchIdRef.current) {
                if (agentResponse?.status === 200) {
                    const rawList = agentResponse.data?.data || agentResponse.data;
                    const agentsList = Array.isArray(rawList) ? rawList : [];
                    const activeAgentExists = agentsList.some((agent: any) => {
                        const statusDisplay = String(agent?.status_display || "").trim().toLowerCase();
                        return statusDisplay === "active";
                    });
                    setHasActiveAgent(activeAgentExists);
                } else {
                    setHasActiveAgent(false);
                }
            }
        } catch (agentError) {
            console.error("Error fetching agent data:", agentError);
            if (currentFetchId === fetchIdRef.current) {
                setHasActiveAgent(false);
            }
        }

        // 2. Fetch Rules data
        try {
            const response = await axiosInstance.get(END_POINTS.RULE_MANAGEMENT_RULES);
            if (currentFetchId === fetchIdRef.current && response?.status === 200) {
                const data = response.data?.rules || response.data?.data || response.data || [];
                setRulesData(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error("Error fetching rules:", error);
        } finally {
            if (currentFetchId === fetchIdRef.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    const handleDeleteRule = async (data: any) => {
        const identifier = data.rule_uuid || data.rule_id;
        try {
            await axiosInstance.delete(`${END_POINTS.RULE_MANAGEMENT_RULES}/${identifier}`);
            fetchRules();
        } catch (error) {
            console.error("Error deleting rule:", error);
        }
    };

    const getScoreDetails = (value: any) => {
        const score = parseFloat(value);
        let label = "N/A";
        let color = "#6c6c6c";
        let bgColor = "#6c6c6c24";

        if (!isNaN(score)) {
            if (score >= 8.0) {
                label = "Critical";
                color = "#670004";
                bgColor = "#67000424";
            } else if (score >= 6.0) {
                label = "High";
                color = "#A7000F";
                bgColor = "#A7000F24";
            } else if (score >= 3.0) {
                label = "Medium";
                color = "#E85800";
                bgColor = "#E8580024";
            } else {
                label = "Low";
                color = "#F2BF00";
                bgColor = "#F2BF0024";
            }
        } else if (typeof value === 'string') {
            const severityLabel = value.toLowerCase();
            label = value;
            switch (severityLabel) {
                case "critical":
                    color = "#670004";
                    bgColor = "#67000424";
                    break;
                case "high":
                    color = "#A7000F";
                    bgColor = "#A7000F24";
                    break;
                case "medium":
                    color = "#E85800";
                    bgColor = "#E8580024";
                    break;
                case "low":
                    color = "#F2BF00";
                    bgColor = "#F2BF0024";
                    break;
            }
        }
        return { label, color, bgColor, score };
    };

    const ruleColumnDefs = [
        {
            field: "id",
            headerName: "#",
            minWidth: 90,
            maxWidth: 120,
            filter: false,
            valueGetter: (params: any) => params.node.rowIndex + 1,
        },
        {
            field: "rule_id",
            headerName: "Rule ID",
            minWidth: 140,
            maxWidth: 150,
            // maxWidth: 200,
            filter: true,
            sortable: true,
            cellRenderer: (params: any) => (
                <WhiteTooltip title={params.value}>
                    <span className="ag-ellipsis" style={{ fontSize: "12px", fontWeight: 500 }}>
                        {params.value || '-'}
                    </span>
                </WhiteTooltip>
            ),
        },
        {
            field: "control_name",
            headerName: "Rule Name",
            filter: false,
            minWidth: 400,
            maxWidth: 500,
            sortable: false,
            cellRenderer: (params: any) => (
                <WhiteTooltip title={params.value}>
                    <span className="ag-ellipsis" style={{ fontSize: "13px", fontWeight: 600 }}>{params.value || '-'}</span>
                </WhiteTooltip>
            ),
        },
        {
            field: "severity",
            headerName: "Severity",
            minWidth: 160,
            maxWidth: 180,
            filter: false,
            sortable: false,
            cellRenderer: (params: any) => {
                const { label, color, bgColor } = getScoreDetails(params.value);

                return (
                    <Chip
                        label={label}
                        size="small"
                        icon={
                            <FiberManualRecordIcon
                                fontSize="small"
                                style={{ color: color, fontSize: 14 }}
                            />
                        }
                        sx={{
                            borderRadius: 1,
                            bgcolor: bgColor,
                            color: color,
                            fontWeight: 600,
                            fontSize: 12,
                            px: 1.5,
                            height: 24,
                        }}
                    />
                );
            },
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "vendor",
            headerName: "Resource",
            minWidth: 150,
            maxWidth: 180,
            filter: false,
            sortable: false,
            cellRenderer: (params: any) => (
                <WhiteTooltip title={
                    params.value
                        ? params.value.toUpperCase()
                        : '-'
                }>
                    <span className="ag-ellipsis" style={{ fontSize: "12px", fontWeight: 500 }}>{
                        params.value
                            ? params.value.toUpperCase()
                            : '-'
                    }</span>
                </WhiteTooltip>
            ),
        },
        {
            field: "rule_type",
            headerName: "Rule Type",
            minWidth: 150,
            maxWidth: 180,
            filter: false,
            sortable: false,
            cellRenderer: (params: any) => (
                <WhiteTooltip title={params.value}>
                    <span className="ag-ellipsis" style={{ fontSize: "12px", fontWeight: 500 }}>{
                        params.value
                            ? params.value.charAt(0).toUpperCase() + params.value.slice(1)
                            : '-'
                    }   </span>
                </WhiteTooltip>
            ),
        },
        // {
        //     field: "tags",
        //     headerName: "Tags",
        //     minWidth: 180,
        //     maxWidth: 200,
        //     filter: false,
        //     sortable: false,
        //     cellRenderer: (params: any) => {
        //         if (!params.value || !Array.isArray(params.value)) return '-';

        //         const visibleTags = params.value.slice(0, 2);
        //         const hiddenTags = params.value.slice(2);

        //         const getTagStyle = (index: number) => {
        //             if (index === 0) {
        //                 return { backgroundColor: "#0183191A", color: "#018319" }; // Green for SSH
        //             } else if (index === 1) {
        //                 return { backgroundColor: "#894B001A", color: "#894B00" }; // Orange for Https
        //             } else {
        //                 return { backgroundColor: "#E0E0E0", color: "#666666" }; // Gray for others
        //             }
        //         };

        //         return (
        //             <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
        //                 {visibleTags.map((tag: string, index: number) => {
        //                     const style = getTagStyle(index);
        //                     return (
        //                         <Chip
        //                             key={index}
        //                             label={tag}
        //                             style={style}
        //                             sx={{ borderRadius: 1, fontWeight: "bold" }}
        //                             size="small"
        //                         />
        //                     );
        //                 })}
        //                 {hiddenTags.length > 0 && (
        //                     <WhiteTooltip title={hiddenTags.join(", ")}>
        //                         <Chip
        //                             label={`+${hiddenTags.length}`}
        //                             style={{ backgroundColor: "#E0E0E0", color: "#666666" }}
        //                             sx={{ borderRadius: 1, fontWeight: "bold" }}
        //                             size="small"
        //                         />
        //                     </WhiteTooltip>
        //                 )}
        //             </Box>
        //         );
        //     },
        //     cellStyle: {
        //         display: "flex",
        //         alignItems: "center",
        //     },
        // },
        {
            field: "risk_score",
            headerName: "Risk Score",
            minWidth: 60,
            // maxWidth: 120,
            filter: false,
            sortable: false,
            cellRenderer: (params: any) => {
                const { color } = getScoreDetails(params.value);
                // Show 0 when the API returns 0 or 0.0 instead of falling back to '-'
                const hasValue = params.value !== null && params.value !== undefined && params.value !== "";
                const displayValue = hasValue ? params.value : '-';
                const tooltipValue = params.value ?? '-';
                return (
                    <WhiteTooltip title={String(tooltipValue)}>
                        <span className="ag-ellipsis" style={{ fontWeight: 700, fontSize: "12px", color: color }}>
                            {displayValue}
                        </span>
                    </WhiteTooltip>
                );
            },
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 150,
            maxWidth: 180,
            filter: false,
            sortable: false,
            cellRenderer: (params: any) => {
                const status = params.value;
                if (status === "enable") {
                    return (
                        <Box
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                width: "100%",
                                height: "100%",
                            }}
                        >
                            <AntSwitch
                                checked={true}
                                onChange={(e) => {
                                    e.stopPropagation();
                                }}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#3A2ADA !important',
                                    },
                                }}
                            />
                        </Box>
                    );
                }
                return (
                    <WhiteTooltip title={params.value.charAt(0).toUpperCase() + params.value.slice(1)

                    }>
                        <span className="ag-ellipsis" style={{ fontSize: "12px", fontWeight: 500 }}>
                            {
                                params.value
                                    ? params.value.charAt(0).toUpperCase() + params.value.slice(1)
                                    : '-'
                            }                        </span>
                    </WhiteTooltip>
                );
            },
        },
        // {
        //     field: "mitre",
        //     headerName: "MITRE",
        //     minWidth: 60,
        //     maxWidth: 100,
        //     filter: false,
        //     sortable: false,
        //     cellRenderer: (params: any) => (
        //         <WhiteTooltip title={params.value}>
        //             <span className="ag-ellipsis" style={{ fontSize: "12px", fontWeight: 500 }}>{params.value || 'No'}</span>
        //         </WhiteTooltip>
        //     ),
        // },
        // {
        //     field: "rule_type",
        //     headerName: "Custom/Pre-Defined",
        //     minWidth: 180,
        //     maxWidth: 200,
        //     filter: false,
        //     sortable: false,
        //     cellRenderer: (params: any) => (
        //         <WhiteTooltip title={params.value}>
        //             <span className="ag-ellipsis" style={{ fontSize: "12px", fontWeight: 500 }}>{params.value || '-'}</span>
        //         </WhiteTooltip>
        //     ),
        // },
        // {
        //     field: "status",
        //     headerName: "Status",
        //     minWidth: 100,
        //     maxWidth: 120,
        //     filter: false,
        //     sortable: false,
        //     headerClass: "status-header-center",
        //     cellClass: "status-cell-center",
        //     cellRenderer: (params: any) => {
        //         const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        //             event.stopPropagation();
        //             // Handle status change here if needed
        //             console.log('Status toggled:', event.target.checked);
        //         };

        //         return (
        //             <Box
        //                 onClick={(e) => e.stopPropagation()}
        //                 sx={{
        //                     display: "flex",
        //                     alignItems: "center",
        //                     justifyContent: "center",
        //                     width: "100%",
        //                     height: "100%",
        //                     margin: 0,
        //                     padding: 0,
        //                 }}
        //             >
        //                 <AntSwitch
        //                     checked={params.value}
        //                     onChange={handleToggle}
        //                     size="small"
        //                     sx={{
        //                         '& .MuiSwitch-switchBase.Mui-checked': {
        //                             '& + .MuiSwitch-track': {
        //                                 backgroundColor: 'primary.main',
        //                                 color: 'primary.contrastText',
        //                                 opacity: 1,
        //                             },
        //                         },
        //                     }}
        //                 />
        //             </Box>
        //         );
        //     },
        //     cellStyle: () => ({
        //         display: "flex",
        //         alignItems: "center",
        //         justifyContent: "center",
        //         padding: 0,
        //         textAlign: "center",
        //     }),
        // },
        {
            headerName: "",
            field: "actions",
            minWidth: 50,
            maxWidth: 50,
            filter: false,
            sortable: false,
            pinned: "right" as const,
            cellRenderer: (params: any) => {
                if (params.data?.status === "draft") {
                    return <ActionMenu params={params} onDelete={handleDeleteRule} />;
                }
                return null;
            },
            cellStyle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            } as any,
        },
    ];

    const [selectedRule, setSelectedRule] = useState<any>(null);

    const handleRowClick = (data: any) => {
        if (data.status === "draft" && hasActiveAgent) {
            setSelectedRule(data);
            setIsAddRuleDrawerOpen(true);
        }
    };

    const handleCloseDrawer = () => {
        setIsAddRuleDrawerOpen(false);
        setSelectedRule(null);
    };

    return (
        <Card
            variant="outlined"
            sx={{ height: "calc(100vh - 103px)", borderRadius: 3 }}
        >
            <AgGridTable
                columnDefs={ruleColumnDefs}
                data={rulesData}
                height={"calc(100vh - 105px)"}
                showSearch
                searchPlaceholder="Search rules"
                showAppsIcon={true}
                isLoading={loading}
                onRowClick={handleRowClick}
                hasActiveAgent={hasActiveAgent}
                onAddRuleClick={() => setIsAddRuleDrawerOpen(true)}
                gridOptions={{
                    rowClassRules: {
                        "status-draft": (params: any) => params.data?.status === "draft",
                    },
                }}
                headerActions={
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ pr: 2 }}
                    >
                        {/* <Box
                            component="img"
                            src={chat_icon}
                            alt="chat icon"
                            sx={{ width: 28, height: 32 }}
                            onClick={() => setIsChatDrawerOpen(true)}
                        /> */}
                        {hasActiveAgent && (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => setIsAddRuleDrawerOpen(true)}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    fontWeight: 700,
                                    px: 2.5,
                                    py: 0.5,
                                }}
                            >
                                Add Rule
                            </Button>
                        )}
                    </Stack>
                }
                styles={{
                    borderRadius: 2,
                    // backgroundColor: "#fff",
                    boxShadow: "0px 8px 24px rgba(15, 23, 55, 0.08)",
                    "& .ag-header-cell.status-header-center": {
                        textAlign: "center !important",
                        display: "flex !important",
                        justifyContent: "center !important",
                        "& .ag-header-cell-text": {
                            margin: "0 auto",
                            textAlign: "center",
                        },
                    },
                    "& .ag-cell.status-cell-center": {
                        textAlign: "center !important",
                        padding: "0 !important",
                        display: "flex !important",
                        alignItems: "center !important",
                        justifyContent: "center !important",
                    },
                    "& .ag-row.status-draft": {
                        cursor: hasActiveAgent ? "pointer !important" : "default !important",
                    },
                    "& .ag-row:not(.status-draft)": {
                        cursor: "default !important",
                    },
                }}
            />
            <AddRuleDrawer
                open={isAddRuleDrawerOpen}
                onClose={handleCloseDrawer}
                onRuleAdded={fetchRules}
                editData={selectedRule}
            />

            <RuleManagementChatDrawer
                open={isChatDrawerOpen}
                onClose={() => setIsChatDrawerOpen(false)}
            />
        </Card>
    );
};

export default RuleManagement;